import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudIcon from "@mui/icons-material/Cloud";
import DataArrayIcon from "@mui/icons-material/DataArray";
import CloseIcon from "@mui/icons-material/Close";
import SecurityIcon from '@mui/icons-material/Security';
import HealthCheck from "../components/dashboard/HealthCheck";
import QueueIcon from '@mui/icons-material/Queue';
import BuildIcon from '@mui/icons-material/Build';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HandymanIcon from '@mui/icons-material/Handyman';

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [toolsOpen, setToolsOpen] = useState(false);

  const mainMenuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
    { text: "Customers", icon: <PeopleIcon />, path: "/customers" },
    {
      text: "Messaging API",
      icon: <SettingsIcon />,
      path: "/messaging-api-configs",
    },
    {
      text: "Salesforce Config",
      icon: <CloudIcon />,
      path: "/salesforce-configs",
    },
    {
      text: "Manage Users",
      icon: <PeopleIcon />,
      path: "/manage-users",
    }
  ];

  const toolsMenuItems = [
    {
      text: "Queue Status",
      icon: <QueueIcon />,
      path: "/queue-status",
    },
    {
      text: "Workers",
      icon: <BuildIcon />,
      path: "/worker-management",
    },
    {
      text: "Request Logs",
      icon: <DataArrayIcon />,
      path: "/request-logs",
    },
    {
      text: "API Settings",
      icon: <SecurityIcon />,
      path: "/api-settings",
    }
  ];

  const handleItemClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleToolsClick = () => {
    setToolsOpen(!toolsOpen);
  };

  const isToolsActive = toolsMenuItems.some(item => location.pathname === item.path);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        backgroundColor: theme.palette.background.paper,
        boxShadow: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header with Logo and Close Button */}
      <Box
        sx={{
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <img
          src="/images/Apistoki-Logo-Rectangular-1.png"
          alt="Logo"
          style={{ 
            width: isMobile ? "140px" : "190px", 
            height: "auto",
            maxWidth: "100%" 
          }}
        />
        {isMobile && onClose && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, marginTop: 1, padding: { xs: 1, md: 2 } }}>
        {/* Main Menu Items */}
        {mainMenuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={handleItemClick}
            sx={{
              padding: { xs: "8px 12px", md: "10px 20px" },
              borderRadius: "8px",
              margin: { xs: "2px 4px", md: "5px 10px" },
              backgroundColor:
                location.pathname === item.path
                  ? theme.palette.primary.main
                  : "transparent",
              color:
                location.pathname === item.path
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },
              "&.Mui-selected": {
                fontWeight: "bold",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.contrastText,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color:
                  location.pathname === item.path
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.primary,
                minWidth: { xs: 36, md: 40 },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: location.pathname === item.path ? "bold" : "normal",
              }}
            />
          </ListItem>
        ))}

        {/* Tools Section */}
        <ListItem
          button
          onClick={handleToolsClick}
          sx={{
            padding: { xs: "8px 12px", md: "10px 20px" },
            borderRadius: "8px",
            margin: { xs: "2px 4px", md: "5px 10px" },
            backgroundColor: isToolsActive ? theme.palette.primary.main : "transparent",
            color: isToolsActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: isToolsActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
              minWidth: { xs: 36, md: 40 },
            }}
          >
            <HandymanIcon />
          </ListItemIcon>
          <ListItemText
            primary="Tools"
            primaryTypographyProps={{
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: isToolsActive ? "bold" : "normal",
            }}
          />
          {toolsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>

        {/* Tools Submenu */}
        <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {toolsMenuItems.map((item) => (
              <ListItem
                button
                key={item.text}
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                onClick={handleItemClick}
                sx={{
                  padding: { xs: "8px 12px", md: "10px 20px" },
                  borderRadius: "8px",
                  margin: { xs: "2px 4px", md: "5px 10px" },
                  marginLeft: { xs: "16px", md: "20px" },
                  backgroundColor:
                    location.pathname === item.path
                      ? theme.palette.primary.main
                      : "transparent",
                  color:
                    location.pathname === item.path
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                  },
                  "&.Mui-selected": {
                    fontWeight: "bold",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                    minWidth: { xs: 36, md: 40 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: { xs: "14px", md: "16px" },
                    fontWeight: location.pathname === item.path ? "bold" : "normal",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>

      {/* Health Check Section */}
      <Box sx={{ padding: { xs: 1, md: 2 } }}>
        <HealthCheck />
      </Box>
    </Box>
  );
};

export default Sidebar;
